import { useRef, useState, type FormEvent } from 'react';
import { Plus, Upload } from 'lucide-react';
import { dateLabel } from '@ck/sdk';
import { api } from './api';
import { Copy, Empty, Failed, Loading, Modal, Note, PageHead, useAction, useApi } from './ui';

/** Reads a CSV file into rows keyed by the header line. Handles quoted fields and ; or , separators. */
export function parseCsv(text: string): Record<string, string>[] {
  const first = text.split('\n')[0] ?? '';
  const delim = first.split(';').length > first.split(',').length ? ';' : ',';
  const rows: string[][] = []; let row: string[] = []; let cell = ''; let q = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (q) { if (ch === '"') { if (text[i + 1] === '"') { cell += '"'; i++; } else q = false; } else cell += ch; continue; }
    if (ch === '"') q = true;
    else if (ch === delim) { row.push(cell); cell = ''; }
    else if (ch === '\n' || ch === '\r') { if (ch === '\r' && text[i + 1] === '\n') i++; row.push(cell); cell = ''; if (row.some((c) => c.trim())) rows.push(row); row = []; }
    else cell += ch;
  }
  row.push(cell); if (row.some((c) => c.trim())) rows.push(row);
  const [head, ...body] = rows;
  return head ? body.map((r) => Object.fromEntries(head.map((h, i) => [h.trim(), (r[i] ?? '').trim()]))) : [];
}

interface Lead { id: string; name: string; email: string; phone: string | null; country: string | null; source: string | null; trader_id: string | null; created_at: string }

/** Contacts the affiliate brings in by hand or from a file. They become referrals when they sign up. */
export function MyClients() {
  const d = useApi(() => api.get<{ leads: Lead[] }>('/v1/partner/leads').then((r) => r.leads));
  const [adding, setAdding] = useState(false);
  const a = useAction();
  const file = useRef<HTMLInputElement>(null);
  async function importFile(f: File) {
    const rows = parseCsv(await f.text());
    if (!rows.length) { a.setError('That file has no rows. The first line should name the columns: name, email, phone, country.'); return; }
    const r = await a.run(() => api.post<{ added: number; skipped: number }>('/v1/partner/leads/import', { rows: rows.slice(0, 5000) }));
    if (r) { a.setDone(`Added ${r.added} contact${r.added === 1 ? '' : 's'}${r.skipped ? `. Skipped ${r.skipped}: missing email, already a client, or already someone’s contact.` : '.'}`); d.reload(); }
    if (file.current) file.current.value = '';
  }
  const list = d.data ?? [];
  return (
    <>
      <PageHead kicker="Partner" title="My clients" lead="Add the traders you work with, one by one or from a spreadsheet. When they sign up with the same email, they count as your referral even if they didn’t use your link.">
        <div className="ph-a">
          <button className="btn ghost" onClick={() => file.current?.click()}><Upload />Import CSV</button>
          <input ref={file} type="file" accept=".csv,text/csv" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void importFile(f); }} />
          <button className="btn" onClick={() => setAdding(true)}><Plus />Add client</button>
        </div>
      </PageHead>
      {a.error && <Note kind="err">{a.error}</Note>}{a.done && <Note>{a.done}</Note>}
      {d.error ? <Failed error={d.error} retry={d.reload} /> : !d.data ? <Loading /> : list.length === 0 ? <Empty title="No clients added yet.">Add one, or import a CSV with columns name, email, phone, country.</Empty> : (
        <div className="tw"><table className="tbl">
          <thead><tr><th>Client</th><th>Phone</th><th>Country</th><th>Status</th><th>Added</th></tr></thead>
          <tbody>{list.map((l) => (
            <tr key={l.id}><th>{l.name}<small className="block">{l.email}</small></th><td>{l.phone ?? '—'}</td><td>{l.country ?? '—'}</td>
              <td>{l.trader_id ? <span className="badge s-approved">Signed up</span> : <span className="badge s-pending">Invited</span>}</td><td>{dateLabel(l.created_at)}</td></tr>
          ))}</tbody>
        </table></div>
      )}
      {adding && <AddClient onClose={(ok) => { setAdding(false); if (ok) d.reload(); }} />}
    </>
  );
}

function AddClient({ onClose }: { onClose: (ok: boolean) => void }) {
  const [f, setF] = useState({ name: '', email: '', phone: '', country: '', notes: '' });
  const a = useAction();
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal title="Add a client" onClose={() => onClose(false)}>
      <form className="form" onSubmit={async (e: FormEvent) => { e.preventDefault(); if (await a.run(() => api.post('/v1/partner/leads', f))) onClose(true); }}>
        <div className="row2"><label>Name<input required value={f.name} onChange={set('name')} /></label><label>Email<input type="email" required value={f.email} onChange={set('email')} /></label></div>
        <div className="row2"><label>Phone<input value={f.phone} onChange={set('phone')} /></label><label>Country<input value={f.country} onChange={set('country')} /></label></div>
        <label>Notes<textarea rows={3} value={f.notes} onChange={set('notes')} /></label>
        {a.error && <Note kind="err">{a.error}</Note>}
        <div className="row-a"><button type="button" className="link" onClick={() => onClose(false)}>Cancel</button><button className="btn" disabled={a.busy}>Add client</button></div>
      </form>
    </Modal>
  );
}

/** Change the link name, e.g. ?ref=AMANI-FX. Old links keep working. */
export function CustomLink({ code, clientUrl, onChanged }: { code: string; clientUrl: string; onChanged: () => void }) {
  const [v, setV] = useState(code);
  const a = useAction();
  return (
    <section className="panel form">
      <h3>Your link name</h3>
      <p className="fine">Make your link easy to remember. Links you already shared keep working.</p>
      <form className="inline-form" onSubmit={async (e) => { e.preventDefault(); if (await a.run(() => api.post('/v1/partner/code', { code: v }), 'Saved. Your new link is ready.')) onChanged(); }}>
        <label className="grow">Link name<input value={v} onChange={(e) => setV(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))} maxLength={24} /></label>
        <button className="btn" disabled={a.busy || v === code || v.length < 3}>Save</button>
      </form>
      <div className="reflink"><span className="mono">{clientUrl}/?ref={v || code}</span><Copy text={`${clientUrl}/?ref=${v || code}`} label="Copy link" /></div>
      {a.error && <Note kind="err">{a.error}</Note>}{a.done && <Note>{a.done}</Note>}
    </section>
  );
}
